import * as yup from 'yup';

import { isValidPostalCode } from 'constants/postalCodeRules';

/**
 * Country-aware postal-code rule for `billingInfo.zip`.
 *
 * Resolves the country the way the API does: the billing country first, falling back to the
 * customer's own country. The select writes the same alpha-3 value into both, but a customer loaded
 * for editing can have them diverge.
 *
 * Only the FORMAT is checked here. Whether the field is required stays with the caller, so the
 * existing LIVE_INSTANCE split is untouched.
 */
const zipFormatRule = (schema) =>
  schema.test('zip-format', 'Invalid postal code for the selected country', function validateZip(value) {
    const billingCountry = this.parent?.country;
    const customerCountry = this.from?.[this.from.length - 1]?.value?.country;
    const country = billingCountry || customerCountry;

    if (isValidPostalCode(value, country)) return true;

    return this.createError({
      message: country ? `Invalid postal code for ${country}` : 'Invalid postal code',
    });
  });

export const NewCustomerBasicInfoValidationSchema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  companyName: yup.string().required('Company name is required'),
  country: yup.string().required('Country is required'),
  workEmail: yup.string().email('Invalid email address').required('Work email is required'),
  phoneNumber: yup
    .string()
    .matches(/^\+?[0-9\s]*$/, 'Phone number must be digits only and can start with a plus sign')
    .optional()
    .nullable(),
  jobTitle: yup.string().required('Job title is required'),
  billingInfo: yup
    .object()
    .when('accountType', {
      is: 'LIVE_INSTANCE',
      then: (schema) =>
        schema.shape({
          department: yup.string().optional().nullable(),
          addressLine1: yup.string().required('Address line 1 is required'),
          addressLine2: yup.string().optional().nullable(),
          addressLine3: yup.string().optional().nullable(),
          state: yup.string().optional().nullable(),
          city: yup.string().required('City is required'),
          zip: zipFormatRule(yup.string().required('Zip code is required')),
          country: yup.string().optional().nullable(),
          taxRegistrationNumber: yup.string().optional().nullable(),
        }),
      otherwise: (schema) =>
        schema.shape({
          department: yup.string().optional().nullable(),
          addressLine1: yup.string().optional().nullable(),
          addressLine2: yup.string().optional().nullable(),
          addressLine3: yup.string().optional().nullable(),
          state: yup.string().optional().nullable(),
          city: yup.string().optional().nullable(),
          zip: zipFormatRule(yup.string().optional().nullable()),
          country: yup.string().optional().nullable(),
          taxRegistrationNumber: yup.string().optional().nullable(),
        }),
    })
    .optional()
    .nullable(),
});

export const NewCustomerSetupAccountValidationSchema = yup.object({
  subdomain: yup
    .string()
    .matches(/^[a-z0-9-]+$/, 'subdomain must be in lowercase and contain only letters, numbers, and hyphens')
    .required('subdomain is required'),
  accountType: yup.string().required(),
});

export const NewCustomerSubscriptionSchema = yup.object({
  periodUnit: yup.string().required('period is required'),
  numberOfUsers: yup.number().when('pricingModel', {
    is: 'PER_UNIT',
    then: (schema) =>
      schema
        .integer('Number of users must be a whole number')
        .min(1, 'Number of users must be at least 1')
        .required('Number of users is required'),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  plan: yup.string().required('package is required'),
  pricingModel: yup.string().optional(),
  unitPrice: yup.number().positive('Unit price must be greater than 0').required('Unit price is required'),
  discount: yup.number().optional().nullable(),
});

export const NewSetupAccountValidationSchema = yup.object({
  customerId: yup.string().required('Customer is required'),
  subdomain: yup
    .string()
    .matches(/^[a-z0-9-]+$/, 'subdomain must be in lowercase and contain only letters, numbers, and hyphens')
    .required('subdomain is required'),
  accountType: yup.string().required(),
});
